import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Menu, X, LogOut, User, ChevronDown, Moon, Sun, Globe } from 'lucide-react';
import { Button } from '../components/ui/button';
import { OwlMini } from '../components/OwlMascot';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';

export const Navbar = () => {
  const { user, logout } = useAuth();
  const { language, switchLanguage, t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [darkMode, setDarkMode] = useState(() => localStorage.getItem('theme') === 'dark');

  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark');
      localStorage.setItem('theme', 'dark');
    } else {
      document.documentElement.classList.remove('dark');
      localStorage.setItem('theme', 'light');
    }
  }, [darkMode]);

  const isActive = (path) => location.pathname === path;

  const navLinks = user ? [
    { path: '/dashboard', label: t('nav_dashboard') },
    { path: '/programs', label: t('nav_programs') },
    { path: '/lexicon', label: t('nav_lexicon') },
    { path: '/chat', label: t('nav_chat') },
    { path: '/badges', label: language === 'fr' ? 'Badges' : 'Badges' },
    { path: '/certificates', label: t('nav_certificates') },
    { path: '/disclaimer', label: language === 'fr' ? 'Avant-propos' : 'Foreword' },
    { path: '/playground', label: language === 'fr' ? 'Playground' : 'Playground' },
    ...(user.role === 'admin' ? [{ path: '/admin', label: language === 'fr' ? 'Admin' : 'Admin' }] : []),
  ] : [
    { path: '/programs', label: t('nav_programs') },
    { path: '/disclaimer', label: language === 'fr' ? 'Avant-propos' : 'Foreword' },
    { path: '/playground', label: 'Playground' },
    { path: '/pricing', label: t('nav_pricing') },
  ];

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  return (
    <nav className="sticky top-0 z-50 glass border-b border-slate-200/50" data-testid="navbar">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2.5 group" data-testid="nav-logo">
            <img
              src="https://customer-assets.emergentagent.com/job_2071b760-1de1-4ab9-81d8-8ca2ec59ffcc/artifacts/lq8u7cy5_LOGO%20IA.png"
              alt="IA Academy"
              className="w-14 h-14 rounded-xl object-cover"
            />
            <OwlMini size={32} className="hidden sm:block" />
            <div className="flex flex-col">
              <span className="font-bold text-lg text-brand-dark tracking-tight leading-none" style={{ fontFamily: 'Poppins' }}>
                IA Academy
              </span>
              <span className="text-[10px] text-gray-400 tracking-wider font-medium leading-none mt-0.5">
                {language === 'fr' ? 'Progressez. 30 min/jour. Résultats concrets.' : 'Level Up. 30 Min a Day. Real Results.'}
              </span>
            </div>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-1">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`nav-link-${link.path.slice(1)}`}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  isActive(link.path)
                    ? 'bg-blue-50 text-blue-700'
                    : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
                }`}
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* Right side */}
          <div className="hidden md:flex items-center gap-3">
            {/* Dark Mode Toggle */}
            <button
              onClick={() => setDarkMode(!darkMode)}
              className="w-9 h-9 rounded-lg flex items-center justify-center border border-slate-200 text-slate-500 hover:text-slate-700 hover:bg-slate-50 transition-colors"
              data-testid="dark-mode-toggle"
              title={darkMode ? 'Light mode' : 'Dark mode'}
            >
              {darkMode ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </button>

            {/* Language Switcher */}
            <div className="flex items-center border border-slate-200 rounded-md overflow-hidden" data-testid="language-switcher">
              <button
                onClick={() => switchLanguage('fr')}
                className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                  language === 'fr' ? 'bg-brand-blue text-white' : 'bg-white text-gray-600 hover:bg-gray-50'
                }`}
                data-testid="lang-fr"
              >
                FR
              </button>
              <button
                onClick={() => switchLanguage('en')}
                className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                  language === 'en' ? 'bg-brand-blue text-white' : 'bg-white text-gray-600 hover:bg-gray-50'
                }`}
                data-testid="lang-en"
              >
                EN
              </button>
            </div>

            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className="gap-2" data-testid="user-menu-trigger">
                    {user.picture ? (
                      <img src={user.picture} alt="" className="w-6 h-6 rounded-full" />
                    ) : (
                      <div className="w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center">
                        <User className="w-3.5 h-3.5 text-blue-600" />
                      </div>
                    )}
                    <span className="text-sm font-medium text-slate-700">{user.name?.split(' ')[0]}</span>
                    <ChevronDown className="w-3 h-3 text-slate-400" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => navigate('/profile')} data-testid="menu-profile">
                    <User className="w-4 h-4 mr-2" />
                    {language === 'fr' ? 'Profil' : 'Profile'}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/dashboard')} data-testid="menu-dashboard">
                    {t('nav_dashboard')}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={handleLogout} data-testid="menu-logout">
                    <LogOut className="w-4 h-4 mr-2" />
                    {t('nav_logout')}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <div className="flex items-center gap-2">
                <Button variant="ghost" size="sm" onClick={() => navigate('/auth')} data-testid="nav-login-btn">
                  {t('nav_login')}
                </Button>
                <Button size="sm" className="bg-brand-orange hover:brightness-110 text-white" onClick={() => navigate('/auth?mode=register')} data-testid="nav-register-btn">
                  {t('nav_register')}
                </Button>
              </div>
            )}
          </div>

          {/* Mobile menu button */}
          <button
            className="md:hidden p-2 rounded-md text-slate-600 hover:bg-slate-100"
            onClick={() => setMobileOpen(!mobileOpen)}
            data-testid="mobile-menu-btn"
          >
            {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="md:hidden border-t border-slate-200 bg-white px-4 py-3 space-y-1" data-testid="mobile-menu">
          {navLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              onClick={() => setMobileOpen(false)}
              className={`block px-3 py-2 rounded-md text-sm font-medium ${
                isActive(link.path) ? 'bg-blue-50 text-blue-700' : 'text-slate-600 hover:bg-slate-50'
              }`}
            >
              {link.label}
            </Link>
          ))}
          <div className="pt-2 border-t border-slate-200 flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={() => switchLanguage(language === 'fr' ? 'en' : 'fr')}>
              <Globe className="w-4 h-4 mr-1" /> {language === 'fr' ? 'EN' : 'FR'}
            </Button>
            {!user && (
              <Button size="sm" className="bg-brand-orange text-white" onClick={() => { navigate('/auth'); setMobileOpen(false); }}>
                {t('nav_login')}
              </Button>
            )}
            {user && (
              <Button variant="ghost" size="sm" onClick={handleLogout}>
                <LogOut className="w-4 h-4 mr-1" /> {t('nav_logout')}
              </Button>
            )}
          </div>
        </div>
      )}
    </nav>
  );
};
